<template>
  <div class="w-100">
    <dashboard-tabs v-if="currentRouteName == 'dashboard'" :selected-year="selectedYear" />

    <div class="w-100 row g-3" v-if="currentRouteName != 'dashboard' && districtsWithSummary">
      <div class="col-12">
        <div class="badge bg-dark-subtle fs-5 px-5 py-3 text-dark">{{ currentRouteName }}</div>
      </div>
      <div class="col-6 col-md-4 col-lg-3" v-for="sum in districtsWithSummary" :key="sum.district.id">
        <div class="border p-3 h-100">
          <div class="card-header fw-bold">
            {{ sum.district.name }}
          </div>
          <div class="card-body">
            <canvas :id="`chart-district-` + sum.district.id" class="h-auto w-100"></canvas>
          </div>
          <div class="w-100 border-top px-3 pt-4 mt-3">
            <div class="d-flex justify-content-between align-items-start">
              <span>Total Allocation</span>
              <b>
                {{ Helper.formatMoney(sum.allocation) }}
              </b>
            </div>
            <div class="d-flex justify-content-between align-items-start">
              <span>Total Used</span>
              <b>
                {{ Helper.formatMoney(sum.used) }}
              </b>
            </div>
            <div class="d-flex justify-content-between align-items-start">
              <span>Total Balance</span>
              <b>
                {{ Helper.formatMoney(sum.allocation - sum.used) }}
              </b>
            </div>
          </div>
        </div>
      </div>
      <div class="case-item justify-content-center text-secondary fst-italic" v-if="!districtsWithSummary.length">
        No data found
      </div>
    </div>
    <loading-spinner v-if="isLoading" />
  </div>
</template>

<script>
import Swal from "sweetalert2";
import Chart from "chart.js/auto";
import LoadingSpinner from "@/components/LoadingSpinner";
import DashboardTabs from "@/views/dashboard/DashboardTabs";

export default {
  props: ["selectedYear"],
  components: {
    LoadingSpinner,
    DashboardTabs,
  },
  computed: {
    currentRouteName() {
      const specialRoutes = ["work-order", "emergency", "electrical"];

      const findRoute = specialRoutes.find((r) => this.$route.name.includes(r));

      return findRoute
        ? this.Helper.kebabToPascalCase(findRoute, true)
        : "dashboard";
    },
  },
  data() {
    return {
      overallChart: null,
      kjpChart: null,
      emergencyChart: null,
      electricalChart: null,
      statusKjpChart: null,
      statusEmergencyChart: null,
      statusElectricalChart: null,
      isLoading: true,
      groupType: null,
      OverallSummary: {
        allocation: 0,
        used: 0,
      },
      KJPSummary: {
        allocation: 0,
        used: 0,
      },
      EmergencySummary: {
        allocation: 0,
        used: 0,
      },
      ElectricalSummary: {
        allocation: 0,
        used: 0,
      },
      StatusKJPSummary: {},
      StatusEmergencySummary: {},
      StatusElectricalSummary: {},
      budgets: [],
      districtsWithSummary: [],
      charts: [],
      chartOptions: {
        plugins: {
          legend: {
            labels: {
              usePointStyle: true,
              boxHeight: 8,
              boxWidth: 8,
            },
          },
        },
      },
    };
  },
  watch: {
    "$route.name": {
      handler: async function () {
        this.groupType = this.$route.name.includes("work-order")
          ? "kjp"
          : this.$route.name.includes("emergency")
            ? "emergency"
            : this.$route.name.includes("electrical")
              ? "electrical"
              : "";

        this.getAllData();
      },
      deep: true,
      immediate: true,
    },
    selectedYear: {
      handler() {
        this.getAllData();
      },
      deep: true,
      immediate: false,
    },
  },
  methods: {
    destroyCharts() {
      return new Promise(async (resolve) => {
        for (let chart of this.charts) {
          await chart.destroy();
        }

        setTimeout(() => {
          this.charts = [];

          resolve();
        }, 500);
      });
    },
    async getNodCases() {
      return new Promise(async (resolve, reject) => {
        let url = this.API.endpoints(this.selectedYear).allCases;

        if (this.groupType) {
          url += `&groupType_contains=${this.groupType}`;
        }

        const [call, error] = await this.Helper.handlePromise(
          this.API.get(url)
        );

        if (error) {
          console.error(error);

          reject(error);
        } else {
          resolve(call.data.filter(d => !d.isRejected));
        }
      });
    },
    async getBudget() {
      return new Promise(async (resolve, reject) => {
        this.KJPSummary.allocation = 0;
        this.ElectricalSummary.allocation = 0;
        this.EmergencySummary.allocation = 0;
        this.OverallSummary.allocation = 0;

        const [call, error] = await this.Helper.handlePromise(
          this.API.get(
            `nod-budgets?year=${this.selectedYear || this.Helper.currentYear}`
          )
        );

        if (error) {
          console.error(error);

          reject(error);
        } else {
          this.budgets = call.data;

          call.data.forEach((data) => {
            this.KJPSummary.allocation += data.kjp || 0;
            this.ElectricalSummary.allocation += data.electrical || 0;
            this.EmergencySummary.allocation += data.emergency || 0;
            this.OverallSummary.allocation = this.KJPSummary.allocation + this.ElectricalSummary.allocation + this.EmergencySummary.allocation;
          });

          resolve();
        }
      });
    },
    plotDistrictChart(nods) {
      this.districtsWithSummary = this.budgets.map((budget) => {
        const _nods = nods
          .filter(
            (nod) =>
              budget.district.id == nod.district &&
              (nod.nod_bq || nod.nod_claim)
          )
          .map((nod) => {
            return { bq: nod.nod_bq, claim: nod.nod_claim };
          });

        const budgetUsed = _nods.reduce((total, nod) => {
          return (
            total + (nod.claim?.amount_used || nod.bq?.amount_allocated || 0)
          );
        }, 0);

        return {
          district: budget.district,
          allocation: budget[this.groupType] || 0,
          used: budgetUsed,
        };
      });

      this.$nextTick(() => {
        this.districtsWithSummary.forEach((budget) => {
          const ctxDistrict = document.getElementById(
            `chart-district-${budget.district.id}`
          );

          let c = new Chart(ctxDistrict, {
            // Doughnut styled like the dashboard Budget tab's utilization
            // donuts (same cutout, bottom legend, RM + share tooltip).
            // Ring width follows Sebut Harga, which sets no cutout at all and
            // so inherits Chart.js's 50% doughnut default (INFS-1423).
            type: "doughnut",
            data: {
              labels: ["Total Balance", "Total Used"],
              datasets: [
                {
                  data: [budget.allocation - budget.used, budget.used],
                  fill: false,
                  // Balance red / Used green. This deliberately does NOT
                  // match Sebut Harga, which codes Belanja/used red and
                  // Baki/balance blue — PR #13 reverted to this coding at
                  // stakeholder request. Do not "fix" it to match SH.
                  backgroundColor: ["#dc3545", "#00d462"],
                },
              ],
            },
            options: {
              ...this.chartOptions,
              cutout: "50%",
              plugins: {
                legend: { position: "bottom", onClick: () => {} },
                tooltip: {
                  callbacks: {
                    label: (ctx) => {
                      const total = ctx.dataset.data.reduce((a, b) => a + (b || 0), 0);
                      const share = total > 0 ? ` (${((ctx.parsed / total) * 100).toFixed(1)}%)` : "";
                      return `${ctx.label}: ${this.Helper.formatMoney(ctx.parsed)}${share}`;
                    },
                  },
                },
              },
            },
          });

          this.charts.push(c);
        });
      });
    },
    async plotDashboard(nods) {
      this.overallChart?.destroy();
      this.kjpChart?.destroy();
      this.emergencyChart?.destroy();
      this.electricalChart?.destroy();

      this.OverallSummary.used = 0;
      this.KJPSummary.used = 0;
      this.ElectricalSummary.used = 0;
      this.EmergencySummary.used = 0;

      this.statusKjpChart?.destroy();
      this.statusEmergencyChart?.destroy();
      this.statusElectricalChart?.destroy();

      this.StatusKJPSummary = {};
      this.StatusEmergencySummary = {};
      this.StatusElectricalSummary = {};

      nods.forEach((nod) => {
        if (nod.groupType == "KJP") {
          this.KJPSummary.used +=
            nod.nod_claim?.amount_used || nod.nod_bq?.amount_allocated || 0;
        } else if (nod.groupType == "Electrical") {
          this.ElectricalSummary.used +=
            nod.nod_claim?.amount_used || nod.nod_bq?.amount_allocated || 0;
        } else if (nod.groupType == "Emergency") {
          this.EmergencySummary.used +=
            nod.nod_claim?.amount_used || nod.nod_bq?.amount_allocated || 0;
        }

        this.OverallSummary.used = this.KJPSummary.used + this.ElectricalSummary.used + this.EmergencySummary.used;

        if (nod.groupType == "KJP") {
          if (nod.isRejected) {
            if (this.StatusKJPSummary.hasOwnProperty('rejected')) this.StatusKJPSummary.rejected += 1;
            else this.StatusKJPSummary['rejected'] = 0;
          } else if (nod.componentStatus?.site_visit !== true) {
            if (this.StatusKJPSummary.hasOwnProperty('site_visit')) this.StatusKJPSummary.site_visit += 1;
            else this.StatusKJPSummary['site_visit'] = 0;
          } else if (nod.componentStatus?.bq && nod.componentStatus?.site_visit === true && (!nod.componentStatus?.memo || !nod.componentStatus?.claim)) {
            if (this.StatusKJPSummary.hasOwnProperty('work_order')) this.StatusKJPSummary.work_order += 1;
            else this.StatusKJPSummary['work_order'] = 0;
          } else if (nod.componentStatus?.site_visit === true && !nod.componentStatus?.bq) {
            if (this.StatusKJPSummary.hasOwnProperty('nod')) this.StatusKJPSummary.nod += 1;
            else this.StatusKJPSummary['nod'] = 0;
          } else if (nod.componentStatus?.claim && nod.componentStatus?.memo) {
            if (this.StatusKJPSummary.hasOwnProperty('completed')) this.StatusKJPSummary.completed += 1;
            else this.StatusKJPSummary['completed'] = 0;
          }
        } else {
          if (nod.isRejected) {
            if (this[`Status${nod.groupType}Summary`].hasOwnProperty('rejected')) this[`Status${nod.groupType}Summary`].rejected += 1;
            else this[`Status${nod.groupType}Summary`]['rejected'] = 0;
          } else if (nod.componentStatus?.site_visit !== true) {
            if (this[`Status${nod.groupType}Summary`].hasOwnProperty('site_visit')) this[`Status${nod.groupType}Summary`].site_visit += 1;
            else this[`Status${nod.groupType}Summary`]['site_visit'] = 0;
          } else if (nod.componentStatus?.site_visit === true && !nod.componentStatus?.bq) {
            if (this[`Status${nod.groupType}Summary`].hasOwnProperty('claims')) this[`Status${nod.groupType}Summary`].claims += 1;
            else this[`Status${nod.groupType}Summary`]['claims'] = 0;
          } else if (nod.componentStatus?.site_visit === true && nod.componentStatus?.bq) {
            if (this[`Status${nod.groupType}Summary`].hasOwnProperty('approved')) this[`Status${nod.groupType}Summary`].approved += 1;
            else this[`Status${nod.groupType}Summary`]['approved'] = 0;
          }
        }
      });

      const ctxOverall = document.getElementById("overallChart");
      this.overallChart = new Chart(ctxOverall, {
        type: "pie",
        data: {
          labels: ["Total Balance", "Total Used"],
          datasets: [
            {
              label: "Overall",
              data: [
                this.OverallSummary.allocation - this.OverallSummary.used,
                this.OverallSummary.used,
              ],
              backgroundColor: ["#dc3545", "#00d462"],
              borderColor: ["rgb(0, 0, 0"],
              borderWidth: 1,
            },
          ],
        },
        options: { ...this.chartOptions },
      });
      const ctxKjp = document.getElementById("kjpChart");
      this.kjpChart = new Chart(ctxKjp, {
        type: "pie",
        data: {
          labels: ["Total Balance", "Total Used"],
          datasets: [
            {
              label: "KJP NOD",
              data: [
                this.KJPSummary.allocation - this.KJPSummary.used,
                this.KJPSummary.used,
              ],
              backgroundColor: ["#dc3545", "#00d462"],
              borderColor: ["rgb(0, 0, 0"],
              borderWidth: 1,
            },
          ],
        },
        options: { ...this.chartOptions },
      });
      const ctxEmergency = document.getElementById("emergencyChart");
      this.emergencyChart = new Chart(ctxEmergency, {
        type: "pie",
        data: {
          labels: ["Total Balance", "Total Used"],
          datasets: [
            {
              label: "Emergency NOD",
              data: [
                this.EmergencySummary.allocation - this.EmergencySummary.used,
                this.EmergencySummary.used,
              ],
              backgroundColor: ["#dc3545", "#00d462"],
              borderColor: ["rgb(0, 0, 0"],
              borderWidth: 1,
            },
          ],
        },
        options: { ...this.chartOptions },
      });
      const ctxElectrical = document.getElementById("electricalChart");
      this.electricalChart = new Chart(ctxElectrical, {
        type: "pie",
        data: {
          labels: ["Total Balance", "Total Used"],
          datasets: [
            {
              label: "Electrical NOD",
              data: [
                this.ElectricalSummary.allocation - this.ElectricalSummary.used,
                this.ElectricalSummary.used,
              ],
              backgroundColor: ["#dc3545", "#00d462"],
              borderColor: ["rgb(0, 0, 0"],
              borderWidth: 1,
            },
          ],
        },
        options: { ...this.chartOptions },
      });

      const ctxStatusKJP = document.getElementById("statusKjpChart");
      this.statusKjpChart = new Chart(ctxStatusKJP, {
        type: "pie",
        data: {
          labels: Object.keys(this.StatusKJPSummary).map(key => key.replace('_', ' ').toUpperCase()),
          datasets: [
            {
              label: "KJP",
              data: Object.values(this.StatusKJPSummary),
              backgroundColor: ["rgb(255, 99, 132)", "rgb(54, 162, 235)", "rgb(201, 175, 148)", "rgb(180, 209, 208)"],
              borderColor: ["rgb(0, 0, 0"],
              borderWidth: 1,
            },
          ],
        },
        options: { ...this.chartOptions },
      });

      const ctxStatusElectrical = document.getElementById("statusElectricalChart");
      this.statusElectricalChart = new Chart(ctxStatusElectrical, {
        type: "pie",
        data: {
          labels: Object.keys(this.StatusElectricalSummary).map(key => key.replace('_', ' ').toUpperCase()),
          datasets: [
            {
              label: "Electrical",
              data: Object.values(this.StatusElectricalSummary),
              backgroundColor: ["rgb(255, 99, 132)", "rgb(54, 162, 235)", "rgb(201, 175, 148)"],
              borderColor: ["rgb(0, 0, 0"],
              borderWidth: 1,
            },
          ],
        },
        options: { ...this.chartOptions },
      });

      const ctxStatusEmergency = document.getElementById("statusEmergencyChart");
      this.statusEmergencyChart = new Chart(ctxStatusEmergency, {
        type: "pie",
        data: {
          labels: Object.keys(this.StatusEmergencySummary).map(key => key.replace('_', ' ').toUpperCase()),
          datasets: [
            {
              label: "Emergency",
              data: Object.values(this.StatusEmergencySummary),
              backgroundColor: ["rgb(255, 99, 132)", "rgb(54, 162, 235)", "rgb(201, 175, 148)"],
              borderColor: ["rgb(0, 0, 0"],
              borderWidth: 1,
            },
          ],
        },
        options: { ...this.chartOptions },
      });

      return;
    },
    toggleLoadingSpinner(value) {
      this.isLoading = value;
    },
    async getAllData() {
      // The main dashboard route renders <dashboard-tabs>, which loads its own data.
      // Only the per-type Summary sub-routes use this component's chart logic.
      // Clear the page-level spinner (it defaults to true) so it doesn't hang here.
      if (this.currentRouteName === "dashboard") {
        this.toggleLoadingSpinner(false);
        return;
      }

      this.toggleLoadingSpinner(true);

      try {
        await this.destroyCharts();

        await this.getBudget();

        const nodCases = await this.getNodCases();

        if (this.currentRouteName == "dashboard") {
          await this.plotDashboard(nodCases);
        } else {
          this.plotDistrictChart(nodCases);
        }

        this.toggleLoadingSpinner(false);
      } catch (error) {
        console.error(error);

        this.toggleLoadingSpinner(false);

        Swal.fire("Error", "Something wrong happened", "error");
      }
    },
  },
};
</script>